import * as amplitude from '@amplitude/analytics-browser';
import { Experiment } from '@amplitude/experiment-js-client';
import { isProd } from './environment';

const API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;
const DEPLOYMENT_KEY = import.meta.env.VITE_AMPLITUDE_DEPLOYMENT_KEY;

let initialized = false;
let experimentClient = null;

export async function initAmplitude () {
  if (initialized || !API_KEY) return;

  amplitude.init(API_KEY, {
    autocapture: {
      elementInteractions: false,
      pageViews: true,
      sessions: true,
    },
    logLevel: isProd() ? amplitude.Types.LogLevel.Error : amplitude.Types.LogLevel.Warn,
  });

  initialized = true;

  // Initialize Experiment client if deployment key is available
  if (DEPLOYMENT_KEY) {
    try {
      experimentClient = Experiment.initializeWithAmplitudeAnalytics(DEPLOYMENT_KEY, {
        automaticExposureTracking: true,
        automaticFetchOnAmplitudeIdentityChange: true,
      });
      await experimentClient.fetch();
    } catch (err) {
      console.warn('[Amplitude Experiment] Failed to fetch flags:', err);
    }
  }
}

export function trackEvent (name, properties) {
  if (!initialized) return;
  amplitude.track(name, properties);
}

export function identifyUser (userId, traits) {
  if (!initialized) return;
  if (userId) {
    amplitude.setUserId(userId);
  }
  if (traits) {
    const identify = new amplitude.Identify();
    Object.entries(traits).forEach(([key, value]) => {
      identify.set(key, value);
    });
    amplitude.identify(identify);
  }
  // Re-fetch experiment flags after identity change
  if (experimentClient) {
    experimentClient.fetch().catch(() => {});
  }
}

// Feature flag helpers
// Resolution: localStorage override → Amplitude Experiment → hardcoded default
const FLAG_DEFAULTS = {
};

export function getFeatureFlag (key) {
  // 1. localStorage override (for debug panel / local dev)
  try {
    const localOverride = localStorage.getItem(key);
    if (localOverride !== null) return localOverride;
  } catch { /* ignore */ }

  // 2. Amplitude Experiment variant
  if (experimentClient) {
    const variant = experimentClient.variant(key);
    if (variant?.value) {
      return variant.value;
    }
  }

  // 3. Hardcoded default
  return FLAG_DEFAULTS[key] ?? null;
}

export function getAllFlags () {
  if (!experimentClient) return {};
  return experimentClient.all();
}

export { amplitude };
